import NSIcon from 'bricks/NSIcon/NSIcon';
import { useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import classNames from 'classnames';
import RenderIf from 'components/RenderIf/RenderIf';
import { ProFormaModelIds } from 'constants/proForma';
import { formatCashFlowPeriods } from 'views/ProFormaTable/helpers';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import ApartmentIncome from './ApartmentIncome/ApartmentIncome';
import RetailOfficeIndustrialIncome from './RetailOfficeIndustrialIncome/RetailOfficeIndustrialIncome';
import DataCenterIncome from './DataCenterIncome/DataCenterIncome';
import StudentHousingIncome from './StudentHousingIncome/StudentHousingIncome';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { useCustomScroll } from '../../../../hooks/useCustomScroll';
import './ProFormaIncome.scss';
import assertNever from 'helpers/assertNever';
import { IFormattedCashFlowPeriod } from 'views/ProFormaTable/types';
import { ApartmentWithRetailIncome } from './ApartmentWithRetailIncome/ApartmentWithRetailIncome';
import { APARTMENT_WITH_RETAIL_INCOME_TABS, RETAIL_TAB_KEY } from 'views/ProFormaTable/constants';
import { NSBadge, NSNavTabs } from 'bricks';
import { GlobalKeys, useLocalStorage } from 'helpers/localStorage';

export interface IProFormaIncomeProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const ProFormaIncome = ({ operationBeginIndex, baseCashFlowColumns }: IProFormaIncomeProps) => {
    const { proForma, cashFlowPeriodsByCadence, operatingInfo } = useProFormaSocketContext();

    const [currentTab, setCurrentTab] = useState<keyof typeof APARTMENT_WITH_RETAIL_INCOME_TABS>('APARTMENT');

    const [apartmentRetailReviewBadgeProformaIds, setApartmentRetailReviewBadgeProformaIds] = useLocalStorage<number[]>(
        GlobalKeys.ApartmentRetailReviewBadgeProformaIds, []
    );
    const showApartmentRetailReviewBadge = apartmentRetailReviewBadgeProformaIds.includes(proForma.id);

    const {
        tableRef, handleTableScroll, createRefHandler,
    } = useCustomScroll('income-table', ['expenses-table', 'permanent-debt-table']);

    useEffect(() => {
        if (!operationBeginIndex) return undefined;

        requestAnimationFrame(() => {
            if (operationBeginIndex > 0) {
                const scrollAmount = operationBeginIndex * 120;
                tableRef.current?.scrollTo({ left: scrollAmount, behavior: 'smooth' });
            }
        });
        return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operationBeginIndex]);

    const monthlyPeriodOptions = useMemo(() => {
        const monthlyPeriodOptions = formatCashFlowPeriods(cashFlowPeriodsByCadence, Periods.MONTHLY);
        if (!operatingInfo?.operationBeginMonth || !operatingInfo?.saleMonth || !monthlyPeriodOptions.length) {
            return [];
        }
        // We do not include the sale month in the options
        return monthlyPeriodOptions.slice(operatingInfo.operationBeginMonth, operatingInfo.saleMonth);
    }, [cashFlowPeriodsByCadence, operatingInfo?.operationBeginMonth, operatingInfo?.saleMonth]);

    const renderIncome = (
        modelId: ProFormaModelIds,
        operationBeginIndex: number,
        baseCashFlowColumns: ColumnDef<any>[],
        monthlyPeriodOptions: IFormattedCashFlowPeriod[]        
    ) => {
        switch (modelId) {
            case ProFormaModelIds.APARTMENT:
                return (
                    <ApartmentIncome
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                );

            case ProFormaModelIds.APARTMENT_WITH_RETAIL:
                return (
                    <ApartmentWithRetailIncome
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                        monthlyPeriodOptions={monthlyPeriodOptions}
                        currentTab={currentTab}
                    />
                );
        
            case ProFormaModelIds.OFFICE:
            case ProFormaModelIds.INDUSTRIAL:
            case ProFormaModelIds.RETAIL:
                return (
                    <RetailOfficeIndustrialIncome
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                        monthlyPeriodOptions={monthlyPeriodOptions}
                    />
                );
        
            case ProFormaModelIds.DATA_CENTER:
                return (
                    <DataCenterIncome
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                        monthlyPeriodOptions={monthlyPeriodOptions}
                    />
                );
            case ProFormaModelIds.STUDENT_HOUSING:
                return (
                    <StudentHousingIncome
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                );
            // TODO: Handle these new property types once they are fully implemented
            case ProFormaModelIds.LAND_DEVELOPMENT:
            case ProFormaModelIds.RESIDENTIAL_CONDO:
            case ProFormaModelIds.HOTEL_HOSPITALITY:
            case ProFormaModelIds.SELF_STORAGE:
                return
            default:
                return assertNever(modelId);
        }
    };

    return (
        <div
            className={classNames(['ProFormaIncome py-3 pl-3', {
                'pr-3': !operationBeginIndex,
            }])}
        >
            <RenderIf isTrue={proForma.proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL}>
                <NSNavTabs
                    justified={false}
                    navItems={Object.entries(APARTMENT_WITH_RETAIL_INCOME_TABS).map(([key, { name, icon }], index) => ({
                        callback: e => {
                            e.preventDefault();
                            setCurrentTab(key as keyof typeof APARTMENT_WITH_RETAIL_INCOME_TABS);
                            
                            if (key === RETAIL_TAB_KEY && showApartmentRetailReviewBadge) {
                                setApartmentRetailReviewBadgeProformaIds(prev => prev.filter(id => id !== proForma.id));
                            }
                        },
                        name,
                        icon: <NSIcon icon={icon} />,
                        isActive: key === currentTab,
                        id: `apartment-with-retail-income-tab-${index}`,
                        className: 'px-4',
                        badge: name === APARTMENT_WITH_RETAIL_INCOME_TABS.RETAIL.name && showApartmentRetailReviewBadge ? 
                            <NSBadge color="primary-lighten" className="ml-2">Review</NSBadge> : undefined,
                    }))}
                />
            </RenderIf>
            <div
                className="scrollable-container"
                ref={createRefHandler(tableRef)}
                onScroll={() => handleTableScroll('table')}
            >
                {renderIncome(proForma.proFormaModelId, operationBeginIndex, baseCashFlowColumns, monthlyPeriodOptions)}
            </div>
        </div>
    );
};

export default ProFormaIncome;
